<template>
  <div class="ai-assistant-widget">
    <transition name="assistant-image-preview">
      <div
        v-if="previewImageUrl"
        class="assistant-image-preview"
        @click="closeImagePreview"
      >
        <img
          class="assistant-image-preview__img"
          :src="previewImageUrl"
          alt="预览图片"
          @click.stop
        />
      </div>
    </transition>

    <transition name="assistant-mask">
      <div v-if="visible" class="assistant-overlay" @click="closePanel"></div>
    </transition>

    <transition name="assistant-panel">
      <aside
        v-if="visible"
        class="assistant-panel"
        :class="panelSizeClass"
      >
        <header class="assistant-panel__header">
          <div class="assistant-panel__title-wrap">
            <p class="assistant-panel__eyebrow">AI 助理</p>
            <h3>智能问答助手</h3>
            <span>支持连续会话、流式回复与历史检索</span>
          </div>
          <div class="assistant-panel__actions">
            <button
              class="assistant-panel__new-chat"
              type="button"
              :disabled="sending || messageLoading"
              @click="startNewConversation"
            >
              新建会话
            </button>
            <div class="assistant-panel__resize">
              <button
                class="assistant-panel__resize-btn"
                type="button"
                :disabled="!canShrinkPanel"
                @click="shrinkPanel"
              >
                <i class="el-icon-minus"></i>
              </button>
              <span class="assistant-panel__resize-text">{{ currentPanelLabel }}</span>
              <button
                class="assistant-panel__resize-btn"
                type="button"
                :disabled="!canExpandPanel"
                @click="expandPanel"
              >
                <i class="el-icon-plus"></i>
              </button>
            </div>
            <button class="assistant-panel__close" type="button" @click="closePanel">
              <i class="el-icon-close"></i>
            </button>
          </div>
        </header>

        <div ref="panelBody" class="assistant-panel__body">
          <section class="assistant-panel__section">
            <div v-if="messageLoading" class="assistant-history assistant-history--state">
              <i class="el-icon-loading"></i>
              <span>正在加载会话消息...</span>
            </div>
            <div v-else-if="!messageList.length" class="assistant-history assistant-history--state">
              <i class="el-icon-chat-line-square"></i>
              <span>暂时还没有消息，先输入一个问题开始对话。</span>
            </div>
            <div v-else ref="messageScroll" class="assistant-message-list">
              <div
                v-for="item in messageList"
                :key="item.id"
                class="assistant-message"
                :class="{
                  'assistant-message--user': item.role === 'user',
                  'assistant-message--assistant': item.role === 'assistant'
                }"
              >
                <span v-if="item.role === 'assistant'" class="assistant-message__avatar">AI</span>
                <div
                  class="assistant-message__bubble"
                  :class="{ 'assistant-message__bubble--pending': item.pending && !item.content }"
                >
                  <template v-for="(block, blockIndex) in buildMessageBlocks(item.content)">
                    <p
                      v-if="block.type === 'text'"
                      :key="`${item.id}-text-${blockIndex}`"
                      class="assistant-message__text"
                    >
                      {{ block.value }}
                    </p>
                    <div
                      v-else
                      :key="`${item.id}-image-${blockIndex}`"
                      class="assistant-message__image-wrap"
                    >
                      <img
                        class="assistant-message__image"
                        :src="block.value"
                        :alt="block.alt || 'AI 图片'"
                        @dblclick.stop="openImagePreview(block.value)"
                      />
                    </div>
                  </template>
                  <span v-if="item.pending" class="assistant-message__status">思考中...</span>
                  <div
                    v-if="item.role === 'assistant' && item.retrieverResources && item.retrieverResources.length"
                    class="assistant-message__refs"
                  >
                    <div class="assistant-message__refs-title">实际命中资料</div>
                    <div
                      v-if="hasWeakRetrieverMatch(item.retrieverResources)"
                      class="assistant-message__refs-warning"
                    >
                      当前命中分数偏低，可能是召回偏了，请重点核对下面的命中片段。
                    </div>
                    <div
                      v-for="resource in item.retrieverResources"
                      :key="resource.key"
                      class="assistant-message__ref-card"
                    >
                      <strong>{{ resource.title }}</strong>
                      <em>{{ buildRetrieverMeta(resource) }}</em>
                      <div
                        v-if="isWeakRetrieverResource(resource)"
                        class="assistant-message__ref-risk"
                      >
                        低分命中，建议核对是否召回到了错误知识片段。
                      </div>
                      <p
                        v-if="shouldShowRetrieverPreview(resource)"
                        class="assistant-message__ref-preview"
                      >
                        {{ resource.contentPreview }}
                      </p>
                      <p v-if="resolveRetrieverContent(resource)" class="assistant-message__ref-content">
                        {{ resolveRetrieverContent(resource) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section v-if="suggestions.length" class="assistant-panel__section">
            <div class="assistant-panel__section-title">快捷提问</div>
            <div class="assistant-panel__chips assistant-panel__chips--compact">
              <button
                v-for="item in suggestions"
                :key="item"
                type="button"
                class="assistant-panel__chip"
                @click="useSuggestion(item)"
              >
                {{ item }}
              </button>
            </div>
          </section>

          <footer class="assistant-panel__composer">
            <div class="assistant-panel__input-wrap">
              <textarea
                ref="composerInput"
                v-model="composerText"
                rows="3"
                placeholder="输入问题后按 Enter 发送，Shift + Enter 换行"
                @keydown.enter.exact.prevent="sendMessage"
              ></textarea>
            </div>
            <div
              v-if="routePreviewEnabled"
              class="assistant-route-preview"
            >
              <div class="assistant-panel__section-head assistant-panel__section-head--compact">
                <div class="assistant-panel__section-title">本次路由预判</div>
                <button
                  class="assistant-panel__link"
                  type="button"
                  :disabled="!canPreviewRoute"
                  @click="previewRoute"
                >
                  {{ routePreviewLoading ? "预判中..." : "预览路由" }}
                </button>
              </div>

              <div v-if="routePreviewError" class="assistant-route-preview__state assistant-route-preview__state--error">
                {{ routePreviewError }}
              </div>

              <div
                v-else-if="routePreviewResult"
                class="assistant-route-preview__card"
              >
                <div class="assistant-route-preview__meta">
                  <span class="assistant-route-preview__badge">{{ routePreviewResult.routeMode || "unknown" }}</span>
                  <span class="assistant-route-preview__tip">仅后端预判，真实命中以聊天返回为准</span>
                </div>
                <p class="assistant-route-preview__reason">{{ routePreviewResult.routeReason || "暂无说明" }}</p>
                <div class="assistant-route-preview__tools">
                  <span
                    v-for="tool in routePreviewResult.toolPlan"
                    :key="tool"
                    class="assistant-route-preview__tool"
                  >
                    {{ tool }}
                  </span>
                </div>
              </div>

              <div v-else class="assistant-route-preview__state">
                输入问题后可先看后端预判的路由方向。
              </div>
            </div>
            <div class="assistant-panel__composer-actions">
              <button
                v-if="isStreaming"
                class="assistant-panel__stop"
                type="button"
                @click="stopStreaming"
              >
                停止生成
              </button>
              <button
                class="assistant-panel__send"
                type="button"
                :disabled="!canSend"
                @click="sendMessage"
              >
                {{ sending ? "发送中..." : "发送" }}
              </button>
            </div>
          </footer>

          <section
            ref="historySection"
            class="assistant-panel__section assistant-panel__section--after-composer"
          >
            <div class="assistant-panel__section-head">
              <div class="assistant-panel__section-title">最近会话</div>
              <div class="assistant-history-head-actions">
                <span class="assistant-history-pager__info">{{ historyRangeText }}</span>
                <button
                  class="assistant-history-pager__btn"
                  type="button"
                  :disabled="historyLoading || !canPrevHistoryPage"
                  @click="loadPrevConversationHistory"
                >
                  上一页
                </button>
                <button
                  class="assistant-history-pager__btn"
                  type="button"
                  :disabled="historyLoading || !canNextHistoryPage"
                  @click="loadNextConversationHistory"
                >
                  下一页
                </button>
                <button
                  class="assistant-panel__link"
                  type="button"
                  :disabled="historyLoading"
                  @click="reloadConversationHistory"
                >
                  刷新
                </button>
              </div>
            </div>

            <div class="assistant-history-toolbar">
              <input
                v-model.trim="historyKeyword"
                class="assistant-history-toolbar__input"
                type="text"
                placeholder="搜索会话关键词"
                @keydown.enter.prevent="searchConversationHistory"
              />
              <button class="assistant-history-toolbar__btn" type="button" @click="searchConversationHistory">
                搜索
              </button>
              <button class="assistant-history-toolbar__btn" type="button" @click="clearConversationSearch">
                清空
              </button>
            </div>

            <div v-if="historyLoading" class="assistant-history assistant-history--state">
              <i class="el-icon-loading"></i>
              <span>正在加载历史会话...</span>
            </div>

            <div v-else-if="historyError" class="assistant-history assistant-history--state">
              <i class="el-icon-warning-outline"></i>
              <span>{{ historyError }}</span>
            </div>

            <div v-else-if="!conversationList.length" class="assistant-history assistant-history--state">
              <i class="el-icon-chat-line-square"></i>
              <span>暂无历史会话。</span>
            </div>

            <div v-else class="assistant-history-list-wrap">
              <div class="assistant-history-list">
                <button
                  v-if="draftConversationCard"
                  type="button"
                  class="assistant-history-card assistant-history-card--draft"
                  :class="{ 'is-active': isConversationActive(draftConversationCard) }"
                  @click="focusDraftConversation"
                >
                  <div class="assistant-history-card__top">
                    <strong>{{ draftConversationCard.title }}</strong>
                    <em>{{ draftConversationCard.timeText }}</em>
                  </div>
                  <p>{{ draftConversationCard.preview }}</p>
                  <span class="assistant-history-card__meta">{{ draftConversationCard.sceneText }}</span>
                </button>
                <button
                  v-for="item in conversationList"
                  :key="item.key"
                  type="button"
                  class="assistant-history-card"
                  :class="{ 'is-active': isConversationActive(item) }"
                  @click="openConversation(item)"
                >
                  <div class="assistant-history-card__top">
                    <strong>{{ item.title }}</strong>
                    <em>{{ item.timeText }}</em>
                  </div>
                  <p>{{ item.preview }}</p>
                  <span class="assistant-history-card__meta">{{ item.sceneText }}</span>
                </button>
              </div>
            </div>
          </section>
        </div>
      </aside>
    </transition>

    <button
      class="assistant-trigger"
      type="button"
      :class="{ 'is-active': visible }"
      @click="togglePanel"
    >
      <span class="assistant-trigger__glow"></span>
      <span class="assistant-trigger__icon">
        <i class="el-icon-chat-dot-round"></i>
      </span>
      <span class="assistant-trigger__text">
        <strong>AI 助理</strong>
        <em>历史会话 / 连续对话</em>
      </span>
    </button>
  </div>
</template>

<script>
import {
  getConversationHistory,
  getConversationMessages,
  getQuickSuggestions,
  getRoutePreview,
  streamSendMessage
} from "@/api/difyChat"

const DEFAULT_SCENE = "BLOG_HOME"
const DEFAULT_MODEL_TYPE = "qwen"
const DEFAULT_THINK_TYPE = "fast"
const DEFAULT_SAP_CODE = "5600"
const SESSION_KEY = `BLOG_AI_ASSISTANT_STATE_${DEFAULT_SCENE}`

export default {
  name: "AiAssistantWidget",
  data() {
    return {
      visible: false,
      historyLoading: false,
      messageLoading: false,
      sending: false,
      historyError: "",
      routePreviewLoading: false,
      routePreviewError: "",
      routePreviewResult: null,
      composerText: "",
      previewImageUrl: "",
      panelSizeIndex: 1,
      panelSizeModes: [
        { key: "compact", label: "小" },
        { key: "default", label: "中" },
        { key: "wide", label: "大" }
      ],
      currentConversationId: "",
      currentConversationTitle: "",
      currentParentMessageId: "",
      conversationList: [],
      messageList: [],
      suggestions: [],
      historyPage: 1,
      // 最近会话固定每页展示 6 条，和面板高度保持一致。
      historyPageSize: 6,
      historyTotal: 0,
      historyKeyword: "",
      historyInitialized: false,
      draftConversationCard: null,
      streamAbortController: null
    }
  },
  computed: {
    panelSizeClass() {
      return `assistant-panel--${this.panelSizeModes[this.panelSizeIndex].key}`
    },
    currentPanelLabel() {
      return this.panelSizeModes[this.panelSizeIndex].label
    },
    canShrinkPanel() {
      return this.panelSizeIndex > 0
    },
    canExpandPanel() {
      return this.panelSizeIndex < this.panelSizeModes.length - 1
    },
    canSend() {
      return !!this.composerText.trim() && !this.sending && !this.messageLoading
    },
    isStreaming() {
      return this.sending
    },
    historyHasMore() {
      return this.conversationList.length < this.historyTotal
    },
    // 统一展示当前会话分页范围，避免用户误以为只能看到固定 6 条。
    historyRangeText() {
      if (!this.historyTotal) {
        return "0 / 0"
      }
      const start = (this.historyPage - 1) * this.historyPageSize + 1
      const end = Math.min(this.historyPage * this.historyPageSize, this.historyTotal)
      return `${start}-${end} / ${this.historyTotal}`
    },
    canPrevHistoryPage() {
      return this.historyPage > 1
    },
    canNextHistoryPage() {
      return this.historyPage * this.historyPageSize < this.historyTotal
    },
    // 路由预览只在开发环境展示，避免把调试信息暴露到生产环境。
    routePreviewEnabled() {
      return process.env.NODE_ENV !== "production"
    },
    canPreviewRoute() {
      return !!this.composerText.trim() && !this.routePreviewLoading && !this.sending && !this.messageLoading
    }
  },
  watch: {
    composerText() {
      this.persistSessionState()
    },
    currentConversationId() {
      this.persistSessionState()
    },
    currentParentMessageId() {
      this.persistSessionState()
    },
    currentConversationTitle() {
      this.persistSessionState()
    },
    panelSizeIndex() {
      this.persistSessionState()
    }
  },
  beforeDestroy() {
    this.stopStreaming()
  },
  mounted() {
    this.restoreSessionState()
  },
  methods: {
    async togglePanel() {
      this.visible = !this.visible
      if (!this.visible) {
        return
      }
      await this.initializePanel()
    },
    async initializePanel() {
      const tasks = []
      if (!this.suggestions.length) {
        tasks.push(this.loadQuickSuggestions())
      }
      if (!this.historyInitialized) {
        tasks.push(this.loadConversationHistory({ reset: true }))
      }
      if (tasks.length) {
        await Promise.all(tasks)
      }

      if (this.currentConversationId && !this.messageList.length) {
        await this.openConversationById(this.currentConversationId, {
          title: this.currentConversationTitle,
          silent: true
        })
      }
    },
    closePanel() {
      this.visible = false
      this.persistSessionState()
    },
    shrinkPanel() {
      if (this.canShrinkPanel) {
        this.panelSizeIndex -= 1
      }
    },
    expandPanel() {
      if (this.canExpandPanel) {
        this.panelSizeIndex += 1
      }
    },
    openImagePreview(url) {
      this.previewImageUrl = url || ""
    },
    closeImagePreview() {
      this.previewImageUrl = ""
    },
    // 清空当前会话上下文，让下一次发送从新的 conversation 开始。
    startNewConversation() {
      if (this.sending || this.messageLoading) {
        return
      }
      this.stopStreaming()
      this.currentConversationId = ""
      this.currentConversationTitle = ""
      this.currentParentMessageId = ""
      this.messageList = []
      this.routePreviewError = ""
      this.routePreviewResult = null
      this.draftConversationCard = this.buildDraftConversationCard()
      this.persistSessionState()
      this.$nextTick(() => {
        if (this.$refs.composerInput) {
          this.$refs.composerInput.focus()
        }
      })
    },
    async loadQuickSuggestions() {
      try {
        const res = await getQuickSuggestions({ questionsScene: DEFAULT_SCENE })
        const data = res && res.data ? res.data.data : []
        const list = Array.isArray(data) ? data : []
        this.suggestions = list.map((item) => String(item || "").trim()).filter(Boolean)
      } catch (error) {
        this.suggestions = []
      }
    },
    async loadConversationHistory({ reset = false } = {}) {
      if (reset) {
        this.historyPage = 1
        this.conversationList = []
      }

      this.historyLoading = true
      this.historyError = ""
      try {
        const res = await getConversationHistory({
          page: this.historyPage,
          pageSize: this.historyPageSize,
          keyword: this.historyKeyword || undefined
        })

        const payload = (res && res.data && res.data.data) || {}
        const list = Array.isArray(payload.list) ? payload.list : []
        const normalized = list.map((item, index) => this.normalizeConversation(item, index))

        // 最近会话改成标准分页，每次只显示当前页数据，避免“加载更多”累积导致误判。
        this.conversationList = normalized

        this.historyTotal = Number(payload.total || 0)
        this.historyInitialized = true
      } catch (error) {
        this.historyError = "历史会话加载失败，请稍后重试。"
      } finally {
        this.historyLoading = false
      }
    },
    async reloadConversationHistory() {
      this.historyPage = 1
      await this.loadConversationHistory({ reset: true })
    },
    // 向前翻页，保留当前搜索条件。
    async loadPrevConversationHistory() {
      if (!this.canPrevHistoryPage || this.historyLoading) {
        return
      }
      this.historyPage -= 1
      await this.loadConversationHistory()
      this.scrollHistorySectionToTop()
    },
    // 向后翻页，保留当前搜索条件。
    async loadNextConversationHistory() {
      if (!this.canNextHistoryPage || this.historyLoading) {
        return
      }
      this.historyPage += 1
      await this.loadConversationHistory()
      this.scrollHistorySectionToTop()
    },
    async searchConversationHistory() {
      this.historyPage = 1
      await this.loadConversationHistory({ reset: true })
    },
    async clearConversationSearch() {
      if (!this.historyKeyword) {
        return
      }
      this.historyKeyword = ""
      this.historyPage = 1
      await this.loadConversationHistory({ reset: true })
    },
    async openConversation(item) {
      if (!item || !item.conversationId) {
        return
      }
      await this.openConversationById(item.conversationId, { title: item.title })
    },
    async openConversationById(conversationId, { title = "", silent = false } = {}) {
      if (!conversationId || this.messageLoading) {
        return
      }

      this.messageLoading = true
      try {
        const res = await getConversationMessages({ conversationId })
        const list = (res && res.data && res.data.data) || []

        this.draftConversationCard = null
        this.currentConversationId = conversationId
        this.currentConversationTitle = title || "历史会话"
        if (!silent) {
          this.currentParentMessageId = ""
        }
        this.messageList = []

        list.forEach((message, index) => {
          if (message.userQuery) {
            this.messageList.push({
              id: `history-user-${index}`,
              role: "user",
              content: message.userQuery
            })
          }
          if (message.answer) {
            this.messageList.push({
              id: `history-assistant-${index}`,
              role: "assistant",
              content: message.answer,
              pending: false
            })
          }
        })

        this.scrollMessagesToBottom()
      } catch (error) {
        this.$message.error("读取会话详情失败，请稍后重试。")
      } finally {
        this.messageLoading = false
      }
    },
    // 调用后端 routePreview，只展示预判结果，不和真实聊天链路混用。
    async previewRoute() {
      const query = this.composerText.trim()
      if (!query || !this.routePreviewEnabled || this.routePreviewLoading) {
        return
      }

      this.routePreviewLoading = true
      this.routePreviewError = ""
      try {
        const res = await getRoutePreview({
          query,
          questionsScene: DEFAULT_SCENE,
          routeMode: "auto"
        })
        const data = res && res.data ? res.data.data : null
        this.routePreviewResult = this.normalizeRoutePreviewResult(data)
      } catch (error) {
        this.routePreviewResult = null
        this.routePreviewError = "路由预判失败，请检查后端服务。"
      } finally {
        this.routePreviewLoading = false
      }
    },
    async sendMessage() {
      const query = this.composerText.trim()
      if (!query || this.sending || this.messageLoading) {
        return
      }

      const userMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: query
      }
      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: "",
        pending: true,
        retrieverResources: []
      }

      if (!this.currentConversationId) {
        this.draftConversationCard = this.buildDraftConversationCard({
          title: this.limitText(query, 28) || "新会话",
          preview: this.limitText(query, 72) || "输入问题后开始新的对话"
        })
      }
      this.messageList.push(userMessage, assistantMessage)
      this.composerText = ""
      // 消息正式发送后，清空上一轮预判结果，避免调试面板展示过期信息。
      this.routePreviewError = ""
      this.routePreviewResult = null
      this.sending = true
      if (!this.currentConversationTitle) {
        this.currentConversationTitle = this.limitText(query, 28)
      }
      this.scrollMessagesToBottom()

      const controller = new AbortController()
      this.streamAbortController = controller

      try {
        await streamSendMessage({
          query,
          conversationId: this.currentConversationId,
          parentMessageId: this.currentParentMessageId || null,
          modelType: DEFAULT_MODEL_TYPE,
          thinkType: DEFAULT_THINK_TYPE,
          sapCode: DEFAULT_SAP_CODE,
          responseMode: "streaming",
          systemLog: "0",
          questionsScene: DEFAULT_SCENE,
          files: []
        }, {
          signal: controller.signal,
          onMessage: (payload) => {
            this.handleStreamMessage(payload, assistantMessage)
          }
        })

        assistantMessage.pending = false
        if (!assistantMessage.content) {
          assistantMessage.content = "已收到请求，但未返回有效内容。"
        }
        await this.reloadConversationHistory()
        this.draftConversationCard = null
      } catch (error) {
        assistantMessage.pending = false
        const aborted = error && (error.name === "AbortError" || /abort/i.test(String(error.message || "")))
        if (aborted) {
          if (!assistantMessage.content) {
            assistantMessage.content = "已停止生成。"
          }
        } else {
          assistantMessage.content = assistantMessage.content || "发送失败，请稍后重试。"
          this.$message.error("发送失败，请检查网络或后端服务。")
        }
      } finally {
        this.sending = false
        if (this.streamAbortController === controller) {
          this.streamAbortController = null
        }
        this.persistSessionState()
        this.scrollMessagesToBottom()
      }
    },
    stopStreaming() {
      if (this.streamAbortController) {
        this.streamAbortController.abort()
        this.streamAbortController = null
      }
    },
    // 草稿会话只用于本地占位，帮助用户确认当前已经切到新会话。
    focusDraftConversation() {
      this.$nextTick(() => {
        if (this.$refs.composerInput) {
          this.$refs.composerInput.focus()
        }
      })
    },
    // 翻页后把面板视口拉回历史会话区顶部，避免还停留在旧页面的中下部位置。
    scrollHistorySectionToTop() {
      this.$nextTick(() => {
        const panelBody = this.$refs.panelBody
        const historySection = this.$refs.historySection
        if (!panelBody || !historySection || typeof panelBody.scrollTo !== "function") {
          return
        }

        const panelRect = panelBody.getBoundingClientRect()
        const historyRect = historySection.getBoundingClientRect()
        const offsetTop = historyRect.top - panelRect.top
        const targetTop = Math.max(panelBody.scrollTop + offsetTop - 8, 0)

        panelBody.scrollTo({
          top: targetTop,
          behavior: "smooth"
        })
      })
    },
    // 当前激活态既要兼容真实会话，也要兼容本地未发送的新会话草稿。
    isConversationActive(item) {
      if (!item) {
        return false
      }
      if (item.isDraft) {
        return !this.currentConversationId
      }
      return !!item.conversationId && item.conversationId === this.currentConversationId
    },
    // 处理后端流式事件：message 用于正文增量，message_end 用于真实命中资料。
    handleStreamMessage(payload, assistantMessage) {
      const source = payload && typeof payload === "object" ? payload : {}
      const eventName = source.event || ""
      const streamData = source.data && typeof source.data === "object" ? source.data : source
      if (eventName === "message_end") {
        assistantMessage.retrieverResources = this.extractRetrieverResources(streamData)
        assistantMessage.pending = false
        this.persistSessionState()
        this.scrollMessagesToBottom()
        return
      }
      if (eventName && eventName !== "message") {
        return
      }
      const answerChunk = streamData.answer || ""
      const conversationId = streamData.conversation_id || streamData.conversationId || ""
      const messageId = streamData.message_id || streamData.messageId || ""

      if (conversationId) {
        this.currentConversationId = conversationId
      }
      if (messageId) {
        this.currentParentMessageId = messageId
      }
      if (answerChunk) {
        assistantMessage.content += answerChunk
      }

      assistantMessage.pending = false
      this.persistSessionState()
      this.scrollMessagesToBottom()
    },
    // 后端已经把 retriever_resources 压缩过，这里只做前端展示字段映射。
    extractRetrieverResources(streamData) {
      const list = Array.isArray(streamData.retrieverResources) ? streamData.retrieverResources : []
      return list.map((item, index) => ({
        key: `${item.documentId || item.title || "resource"}-${index}`,
        title: item.title || item.documentName || "未命名文档",
        datasetName: item.datasetName || "",
        section: item.section || "",
        path: item.path || "",
        score: this.normalizeRetrieverScore(item.score),
        contentPreview: item.contentPreview || "",
        content: item.content || ""
      }))
    },
    // 召回 top score 偏低时直接提示，避免把偏召回误当成“系统回答不全”。
    hasWeakRetrieverMatch(retrieverResources) {
      const scores = (Array.isArray(retrieverResources) ? retrieverResources : [])
        .map((item) => this.normalizeRetrieverScore(item.score))
        .filter((score) => score > 0)
      if (!scores.length) {
        return false
      }
      return scores.some((score) => score < 0.5)
    },
    // 展示知识来源、章节和分数，方便快速判断命中是否靠谱。
    buildRetrieverMeta(resource) {
      const parts = []
      if (resource.datasetName) {
        parts.push(resource.datasetName)
      }
      if (resource.section) {
        parts.push(resource.section)
      } else if (resource.path) {
        parts.push(resource.path)
      }
      if (resource.score > 0) {
        parts.push(`score ${resource.score.toFixed(4)}`)
      }
      return parts.join(" · ") || "未命名知识源"
    },
    // 优先展示完整命中片段；如果后端还是旧结构，则回退到摘要字段。
    resolveRetrieverContent(resource) {
      return resource.content || resource.contentPreview || ""
    },
    // 单条命中低于阈值时单独打标，便于快速定位哪一条召回偏了。
    isWeakRetrieverResource(resource) {
      return this.normalizeRetrieverScore(resource && resource.score) > 0
        && this.normalizeRetrieverScore(resource && resource.score) < 0.5
    },
    // 如果完整片段和摘要相同，就不重复展示两遍。
    shouldShowRetrieverPreview(resource) {
      const preview = String((resource && resource.contentPreview) || "").trim()
      const content = String((resource && resource.content) || "").trim()
      if (!preview) {
        return false
      }
      return !content || preview !== content
    },
    // 分数统一转成 number，避免模板里再做类型兼容。
    normalizeRetrieverScore(score) {
      const value = Number(score)
      return Number.isFinite(value) ? value : 0
    },
    // 统一整理预判返回结构，避免模板里直接兼容后端空值。
    normalizeRoutePreviewResult(data) {
      const source = data && typeof data === "object" ? data : {}
      const toolPlan = Array.isArray(source.toolPlan) ? source.toolPlan : []
      return {
        routeMode: source.routeMode || "",
        routeReason: source.routeReason || "",
        toolPlan
      }
    },
    useSuggestion(text) {
      this.composerText = text
      this.$nextTick(() => {
        if (this.$refs.composerInput) {
          this.$refs.composerInput.focus()
        }
      })
    },
    scrollMessagesToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messageScroll
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    buildMessageBlocks(content) {
      const source = String(content || "")
      if (!source.trim()) {
        return []
      }

      const imagePattern = /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)|(?:^|\n)(https?:\/\/[^\s]+\.(?:png|jpg|jpeg|gif|webp|bmp|svg))(?:\n|$)/gi
      const blocks = []
      let lastIndex = 0
      let match = imagePattern.exec(source)

      while (match) {
        const startIndex = match.index
        const imageUrl = match[2] || match[3] || ""
        const alt = match[1] || ""

        if (startIndex > lastIndex) {
          blocks.push({
            type: "text",
            value: source.slice(lastIndex, startIndex).trim()
          })
        }

        blocks.push({
          type: "image",
          value: imageUrl,
          alt
        })

        lastIndex = startIndex + match[0].length
        match = imagePattern.exec(source)
      }

      if (lastIndex < source.length) {
        blocks.push({
          type: "text",
          value: source.slice(lastIndex).trim()
        })
      }

      return blocks.filter((item) => item.type === "image" || item.value)
    },
    normalizeConversation(item, index) {
      const query = this.pickFirstValue(item, [
        "latestQuestion",
        "latest_question",
        "userQuery",
        "user_query",
        "title"
      ])
      const answer = this.pickFirstValue(item, [
        "latestAnswer",
        "latest_answer",
        "answer",
        "answerText",
        "answer_text"
      ])
      const timeValue = this.pickFirstValue(item, [
        "answerTime",
        "answer_time",
        "queryTime",
        "query_time",
        "lastUpd",
        "last_upd",
        "created"
      ])
      const sceneValue = this.pickFirstValue(item, [
        "questionsScene",
        "QUESTIONS_SCENE",
        "scene"
      ])
      const conversationId = this.pickFirstValue(item, [
        "conversationId",
        "conversation_id"
      ])

      return {
        key: conversationId || `conversation-${index}`,
        conversationId: conversationId || "",
        title: this.limitText(query || "未命名会话", 28),
        preview: this.limitText(answer || query || "暂无会话内容", 72),
        timeText: this.formatDateTime(timeValue),
        sceneText: sceneValue || "ALL"
      }
    },
    buildDraftConversationCard({ title = "新会话", preview = "输入问题后开始新的对话" } = {}) {
      return {
        key: `draft-${Date.now()}`,
        conversationId: "",
        title,
        preview,
        timeText: "刚刚",
        sceneText: "NEW",
        isDraft: true
      }
    },
    pickFirstValue(source, fields) {
      for (let i = 0; i < fields.length; i += 1) {
        const key = fields[i]
        const value = source ? source[key] : ""
        if (value !== undefined && value !== null && value !== "") {
          return value
        }
      }
      return ""
    },
    formatDateTime(value) {
      if (!value) {
        return "--"
      }
      const text = String(value).replace("T", " ")
      return text.length > 16 ? text.slice(0, 16) : text
    },
    limitText(text, maxLength) {
      const value = String(text || "").trim()
      if (!value) {
        return ""
      }
      return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value
    },
    persistSessionState() {
      const state = {
        conversationId: this.currentConversationId,
        conversationTitle: this.currentConversationTitle,
        parentMessageId: this.currentParentMessageId,
        draft: this.composerText,
        panelSizeIndex: this.panelSizeIndex,
        savedAt: Date.now()
      }
      try {
        localStorage.setItem(SESSION_KEY, JSON.stringify(state))
      } catch (error) {
        // ignore
      }
    },
    restoreSessionState() {
      try {
        const raw = localStorage.getItem(SESSION_KEY)
        if (!raw) {
          return
        }
        const state = JSON.parse(raw)
        this.currentConversationId = String(state.conversationId || "")
        this.currentConversationTitle = String(state.conversationTitle || "")
        this.currentParentMessageId = String(state.parentMessageId || "")
        this.composerText = String(state.draft || "")
        const idx = Number(state.panelSizeIndex)
        if (!Number.isNaN(idx) && idx >= 0 && idx < this.panelSizeModes.length) {
          this.panelSizeIndex = idx
        }
      } catch (error) {
        // ignore
      }
    }
  }
}
</script>
<style scoped>
.ai-assistant-widget {
  position: fixed;
  right: 24px;
  bottom: 28px;
  z-index: 3000;
}

.assistant-overlay {
  position: fixed;
  inset: 0;
  background: rgba(6, 10, 18, 0.52);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 3000;
}

.assistant-image-preview {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: rgba(3, 6, 14, 0.82);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.assistant-image-preview__img {
  display: block;
  max-width: min(92vw, 1200px);
  max-height: 90vh;
  border-radius: 18px;
  border: 1px solid rgba(0, 255, 245, 0.2);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  object-fit: contain;
  background: rgba(6, 10, 18, 0.92);
}

.assistant-panel {
  position: fixed;
  right: 24px;
  top: 86px;
  bottom: 118px;
  width: min(420px, calc(100vw - 48px));
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(0, 255, 245, 0.22);
  background:
    radial-gradient(circle at top right, rgba(0, 255, 245, 0.12), transparent 30%),
    radial-gradient(circle at 20% 0%, rgba(236, 72, 153, 0.18), transparent 24%),
    linear-gradient(180deg, rgba(10, 15, 28, 0.98) 0%, rgba(12, 10, 26, 0.98) 100%);
  box-shadow:
    0 24px 60px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(236, 72, 153, 0.08);
  z-index: 3001;
}

.assistant-panel--compact {
  width: min(360px, calc(100vw - 48px));
}

.assistant-panel--default {
  width: min(420px, calc(100vw - 48px));
}

.assistant-panel--wide {
  width: min(560px, calc(100vw - 48px));
  top: 74px;
  bottom: 96px;
}

.assistant-panel__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.assistant-panel__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 72px 18px 22px;
  border-bottom: 1px solid rgba(0, 255, 245, 0.12);
}

.assistant-panel__actions {
  position: absolute;
  top: 16px;
  right: 18px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.assistant-panel__new-chat {
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid rgba(236, 72, 153, 0.18);
  background: rgba(236, 72, 153, 0.12);
  color: rgba(251, 207, 232, 0.96);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.assistant-panel__new-chat:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.assistant-panel__resize {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 999px;
  border: 1px solid rgba(0, 255, 245, 0.14);
  background: rgba(255, 255, 255, 0.04);
}

.assistant-panel__resize-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: rgba(0, 255, 245, 0.08);
  color: rgba(224, 242, 254, 0.94);
  cursor: pointer;
}

.assistant-panel__resize-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.assistant-panel__resize-text {
  min-width: 18px;
  color: rgba(203, 213, 225, 0.88);
  font-size: 12px;
  text-align: center;
}

.assistant-panel__title-wrap h3 {
  margin: 4px 0 8px;
  color: rgba(224, 242, 254, 0.98);
  font-size: 22px;
  line-height: 1.2;
}

.assistant-panel__title-wrap span,
.assistant-panel__eyebrow,
.assistant-panel__section-title,
.assistant-panel__link,
.assistant-panel__section-tip {
  color: rgba(148, 163, 184, 0.9);
}

.assistant-panel__eyebrow {
  margin: 0;
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.assistant-panel__close {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  border-radius: 12px;
  border: 1px solid rgba(0, 255, 245, 0.16);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(224, 242, 254, 0.92);
  cursor: pointer;
  z-index: 2;
}

.assistant-panel__hero,
.assistant-panel__section,
.assistant-panel__composer {
  padding-left: 22px;
  padding-right: 22px;
}

.assistant-panel__hero {
  padding-top: 20px;
  padding-bottom: 18px;
}

.assistant-panel__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(0, 255, 245, 0.1);
  color: #00fff5;
  font-size: 12px;
  font-weight: 600;
}

.assistant-panel__hero h4 {
  margin: 14px 0 10px;
  color: rgba(224, 242, 254, 0.98);
  font-size: 19px;
  line-height: 1.5;
}

.assistant-panel__hero p {
  color: rgba(203, 213, 225, 0.86);
  font-size: 13px;
  line-height: 1.8;
}

.assistant-panel__section {
  padding-top: 16px;
}

.assistant-panel__section--after-composer {
  padding-top: 18px;
}

.assistant-panel__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.assistant-panel__section-head--compact {
  margin-bottom: 8px;
}

.assistant-panel__section-title {
  font-size: 13px;
  letter-spacing: 0.08em;
}

.assistant-panel__conversation-name {
  margin: 6px 0 0;
  color: rgba(224, 242, 254, 0.96);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}

.assistant-panel__section-tip {
  font-size: 12px;
}

.assistant-panel__link {
  border: none;
  background: transparent;
  font-size: 12px;
  cursor: pointer;
}

.assistant-panel__link:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.assistant-history,
.assistant-history-card,
.assistant-message__bubble,
.assistant-panel__chip,
.assistant-panel__input-wrap {
  border: 1px solid rgba(0, 255, 245, 0.14);
  background: rgba(255, 255, 255, 0.04);
}

.assistant-history--state {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 88px;
  padding: 0 14px;
  border-radius: 16px;
  color: rgba(203, 213, 225, 0.88);
  font-size: 13px;
}

.assistant-message-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 280px;
  overflow-y: auto;
  padding-right: 4px;
}

.assistant-message {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.assistant-message--user {
  justify-content: flex-end;
}

.assistant-message__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(0, 255, 245, 0.18), rgba(236, 72, 153, 0.2));
  color: rgba(224, 242, 254, 0.98);
  font-size: 12px;
  font-weight: 700;
}

.assistant-message__bubble {
  max-width: 86%;
  padding: 12px 14px;
  border-radius: 16px;
}

.assistant-message__bubble--pending {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 92px;
  min-height: 44px;
  padding: 10px 14px;
}

.assistant-message__text {
  margin: 0;
  color: rgba(224, 242, 254, 0.94);
  font-size: 13px;
  line-height: 1.75;
  white-space: pre-wrap;
  word-break: break-word;
}

.assistant-message__text + .assistant-message__image-wrap,
.assistant-message__image-wrap + .assistant-message__text,
.assistant-message__image-wrap + .assistant-message__image-wrap {
  margin-top: 10px;
}

.assistant-message__image-wrap {
  width: 100%;
}

.assistant-message__image {
  display: block;
  max-width: 100%;
  max-height: 240px;
  border-radius: 14px;
  border: 1px solid rgba(0, 255, 245, 0.14);
  object-fit: contain;
  background: rgba(6, 10, 18, 0.5);
}

.assistant-message--user .assistant-message__bubble {
  background: linear-gradient(135deg, rgba(0, 255, 245, 0.14), rgba(236, 72, 153, 0.16));
  border-color: rgba(236, 72, 153, 0.22);
}

.assistant-message__status {
  display: inline-flex;
  margin-top: 8px;
  color: rgba(148, 163, 184, 0.9);
  font-size: 12px;
}

.assistant-message__refs {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.assistant-message__refs-title {
  color: rgba(148, 163, 184, 0.9);
  font-size: 12px;
}

.assistant-message__refs-warning {
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid rgba(251, 191, 36, 0.18);
  background: rgba(120, 53, 15, 0.22);
  color: rgba(253, 224, 71, 0.96);
  font-size: 12px;
  line-height: 1.6;
}

.assistant-message__ref-card {
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(0, 255, 245, 0.12);
  background: rgba(255, 255, 255, 0.03);
}

.assistant-message__ref-card strong {
  display: block;
  color: rgba(224, 242, 254, 0.95);
  font-size: 12px;
  line-height: 1.5;
}

.assistant-message__ref-card em {
  display: block;
  margin-top: 2px;
  color: rgba(148, 163, 184, 0.88);
  font-size: 11px;
  font-style: normal;
}

.assistant-message__ref-card p {
  margin: 6px 0 0;
  color: rgba(203, 213, 225, 0.86);
  font-size: 12px;
  line-height: 1.6;
}

.assistant-message__ref-preview {
  color: rgba(148, 163, 184, 0.9);
}

.assistant-message__ref-risk {
  margin-top: 6px;
  padding: 6px 8px;
  border-radius: 10px;
  border: 1px solid rgba(248, 113, 113, 0.18);
  background: rgba(127, 29, 29, 0.16);
  color: rgba(253, 186, 186, 0.96);
  font-size: 12px;
  line-height: 1.5;
}

.assistant-message__ref-content {
  white-space: pre-wrap;
  word-break: break-word;
}

.assistant-message__bubble--pending .assistant-message__status {
  margin-top: 0;
}

.assistant-history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* 历史会话按页展示时，不再额外限制列表自身高度，避免当前页 6 条还要再出现内层滚动。 */
  overflow: visible;
  padding-right: 4px;
  padding-left: 10px;
  padding-bottom: 4px;
}

.assistant-history-list-wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.assistant-history-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.assistant-history-toolbar__input {
  flex: 1;
  height: 32px;
  border: 1px solid rgba(0, 255, 245, 0.14);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  padding: 0 10px;
  color: rgba(224, 242, 254, 0.94);
  outline: none;
}

.assistant-history-toolbar__input::placeholder {
  color: rgba(148, 163, 184, 0.84);
}

.assistant-history-toolbar__btn {
  height: 32px;
  border: 1px solid rgba(0, 255, 245, 0.2);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(224, 242, 254, 0.92);
  padding: 0 10px;
  cursor: pointer;
}

.assistant-history-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.assistant-history-head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.assistant-history-pager__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.assistant-history-pager__info {
  color: rgba(148, 163, 184, 0.84);
  font-size: 12px;
}

.assistant-history-pager__btn {
  border: 1px solid rgba(0, 255, 245, 0.2);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(224, 242, 254, 0.92);
  padding: 6px 10px;
  cursor: pointer;
}

.assistant-history-pager__btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.assistant-history-card {
  width: calc(100% - 14px);
  padding: 12px;
  border-radius: 14px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.assistant-history-card:hover,
.assistant-history-card.is-active {
  border-color: rgba(0, 255, 245, 0.28);
  background: rgba(0, 255, 245, 0.08);
  transform: translateY(-1px);
}

.assistant-history-card--draft {
  border-style: dashed;
}

.assistant-history-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
}

.assistant-history-card__top strong {
  color: rgba(224, 242, 254, 0.96);
  font-size: 13px;
  line-height: 1.5;
}

.assistant-history-card__top em {
  flex-shrink: 0;
  color: rgba(148, 163, 184, 0.84);
  font-size: 11px;
  font-style: normal;
}

.assistant-history-card p {
  margin: 0;
  color: rgba(203, 213, 225, 0.86);
  font-size: 12px;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.assistant-history-card__meta {
  display: inline-flex;
  margin-top: 8px;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(0, 255, 245, 0.08);
  color: #8ffaf5;
  font-size: 11px;
}

.assistant-panel__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.assistant-panel__chips--compact {
  gap: 8px;
}

.assistant-panel__chip {
  padding: 7px 12px;
  border-radius: 999px;
  color: rgba(224, 242, 254, 0.92);
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
}

.assistant-panel__composer {
  margin-top: auto;
  padding-top: 18px;
  padding-bottom: 22px;
  border-top: 1px solid rgba(0, 255, 245, 0.12);
}

.assistant-route-preview {
  margin-bottom: 12px;
  padding: 12px 14px;
  border: 1px solid rgba(0, 255, 245, 0.12);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
}

.assistant-route-preview__card,
.assistant-route-preview__state {
  border-radius: 12px;
}

.assistant-route-preview__state {
  padding: 10px 12px;
  color: rgba(148, 163, 184, 0.9);
  font-size: 12px;
  line-height: 1.6;
  background: rgba(255, 255, 255, 0.02);
}

.assistant-route-preview__state--error {
  color: rgba(248, 113, 113, 0.96);
  border: 1px solid rgba(248, 113, 113, 0.18);
  background: rgba(127, 29, 29, 0.18);
}

.assistant-route-preview__card {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.assistant-route-preview__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.assistant-route-preview__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(0, 255, 245, 0.12);
  color: #8ffaf5;
  font-size: 12px;
  font-weight: 700;
  text-transform: lowercase;
}

.assistant-route-preview__tip {
  color: rgba(148, 163, 184, 0.84);
  font-size: 11px;
  text-align: right;
}

.assistant-route-preview__reason {
  margin: 0;
  color: rgba(224, 242, 254, 0.92);
  font-size: 12px;
  line-height: 1.7;
}

.assistant-route-preview__tools {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.assistant-route-preview__tool {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(236, 72, 153, 0.2);
  background: rgba(236, 72, 153, 0.1);
  color: rgba(251, 207, 232, 0.95);
  font-size: 11px;
  line-height: 1.4;
}

.assistant-panel__input-wrap {
  margin-bottom: 12px;
  border-radius: 18px;
}

.assistant-panel__input-wrap textarea {
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  padding: 14px 16px;
  color: rgba(224, 242, 254, 0.94);
  font-size: 13px;
  line-height: 1.7;
}

.assistant-panel__input-wrap textarea::placeholder {
  color: rgba(148, 163, 184, 0.84);
}

.assistant-panel__composer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.assistant-panel__stop {
  height: 46px;
  border: 1px solid rgba(236, 72, 153, 0.4);
  border-radius: 14px;
  background: rgba(236, 72, 153, 0.12);
  color: rgba(248, 113, 113, 0.98);
  font-size: 13px;
  font-weight: 700;
  padding: 0 14px;
  cursor: pointer;
}

.assistant-panel__send {
  flex: 1;
  width: auto;
  height: 46px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #00fff5 0%, #ec4899 100%);
  color: #08111f;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  cursor: pointer;
}

.assistant-panel__send:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.assistant-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 210px;
  padding: 14px 18px 14px 14px;
  border-radius: 20px;
  border: 1px solid rgba(0, 255, 245, 0.24);
  background: rgba(10, 15, 28, 0.9);
  box-shadow:
    0 18px 44px rgba(0, 0, 0, 0.32),
    0 0 0 1px rgba(236, 72, 153, 0.08);
  cursor: pointer;
  z-index: 3002;
}

.assistant-trigger.is-active {
  border-color: rgba(236, 72, 153, 0.34);
}

.assistant-trigger__glow {
  position: absolute;
  left: 16px;
  bottom: 8px;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 255, 245, 0.28) 0%, rgba(0, 255, 245, 0) 72%);
  pointer-events: none;
}

.assistant-trigger__icon {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(0, 255, 245, 0.16), rgba(236, 72, 153, 0.2));
  color: rgba(224, 242, 254, 0.98);
  font-size: 20px;
}

.assistant-trigger__text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  color: rgba(224, 242, 254, 0.96);
}

.assistant-trigger__text strong {
  font-size: 15px;
  font-weight: 700;
}

.assistant-trigger__text em {
  color: rgba(148, 163, 184, 0.88);
  font-size: 12px;
  font-style: normal;
}

.assistant-panel-enter-active,
.assistant-panel-leave-active,
.assistant-mask-enter-active,
.assistant-mask-leave-active {
  transition: all 0.24s ease;
}

.assistant-panel-enter,
.assistant-panel-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.98);
}

.assistant-mask-enter,
.assistant-mask-leave-to {
  opacity: 0;
}

.assistant-image-preview-enter-active,
.assistant-image-preview-leave-active {
  transition: opacity 0.2s ease;
}

.assistant-image-preview-enter,
.assistant-image-preview-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .ai-assistant-widget {
    right: 12px;
    left: 12px;
    bottom: 12px;
  }

  .assistant-trigger {
    width: 100%;
    min-width: 0;
    justify-content: center;
  }

  .assistant-panel {
    right: 12px;
    left: 12px;
    top: 74px;
    bottom: 84px;
    width: auto;
  }
}
</style>
